/**
 * StructureScreen — validación de la estructura de la reunión (Fase 49-50).
 *
 * Fase 49 — esqueleto: carga structure.json, muestra 3 tabs
 *   (Segmentación, Decisiones, Tareas) con los datos sin acciones.
 * Fase 50 — Tab Segmentación: timeline editable, click → audio,
 *   renombrar, marcar no-relevante, agregar/eliminar secciones.
 * Fase 51 — Tab Decisiones: validación de decisiones.
 * Fase 52 — Tab Tareas: validación de tareas + confirmación final.
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { useAtom, useAtomValue } from "jotai";
import styled from "styled-components";

import {
  currentProcessingIdAtom,
  structureDocAtom,
  structureErrorAtom,
  structureLoadingAtom,
  structureSectionEditsAtom,
  structureDecisionEditsAtom,
  structureTaskEditsAtom,
  structureTabAtom,
} from "../../state/atoms.ts";
import type { SectionEdit, DecisionEdit, TaskEdit } from "../../state/atoms.ts";
import type {
  StructureDoc,
  MeetingSection,
} from "../../lib/types.ts";
import {
  getProcessing,
  getProcessingFile,
  fetchTextFromPar,
  confirmStructure,
  fetchStructureEdits,
  saveStructureEdits,
  submitMetrics,
} from "../../platform/api.ts";
import { useSamplePlayer } from "../hooks/useSamplePlayer.ts";
import { useStepTimer } from "../hooks/useStepTimer.ts";
import { useNavigateTo, useSyncParams } from "../routing.ts";
import {
  MeetingTimeline,
  SECTION_TYPE_COLORS,
} from "../components/MeetingTimeline.tsx";
import { DecisionCard } from "../components/DecisionCard.tsx";
import { TaskCard } from "../components/TaskCard.tsx";
import { Button } from "../components/Button.tsx";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.font.size.xl};
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  border: none;
  border-bottom: 2px solid
    ${({ theme, $active }) =>
      $active ? theme.color.accent : "transparent"};
  background: none;
  color: ${({ theme, $active }) =>
    $active ? theme.color.accent : theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme, $active }) =>
    $active ? theme.font.weight.medium : theme.font.weight.normal};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.color.accent};
  }
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.space.sm};
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  color: ${({ theme }) => theme.color.textMuted};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.space.sm};
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  vertical-align: middle;
`;

const Badge = styled.span<{ $color: string }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $color }) => $color};
  color: #fff;
  white-space: nowrap;
`;

const Empty = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;

const Loading = styled.p`
  color: ${({ theme }) => theme.color.textMuted};
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.color.error};
  background: ${({ theme }) => theme.color.errorMuted};
  padding: ${({ theme }) => theme.space.md};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const CountBadge = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin-left: ${({ theme }) => theme.space.xs};
`;

// ── Fase 50 — nuevos styled ────────────────────────────────────────────────

const PlayButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.accent};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: 2px 6px;
  line-height: 1;

  &:hover {
    background: ${({ theme }) => theme.color.accentMuted};
  }
`;

const InlineInput = styled.input`
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: 2px 4px;
  border: 1px solid ${({ theme }) => theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  width: 100%;
  box-sizing: border-box;
`;

const ClickableTitle = styled.span`
  cursor: text;
  border-bottom: 1px dashed transparent;

  &:hover {
    border-bottom-color: ${({ theme }) => theme.color.textMuted};
  }
`;

const RelevanceBadge = styled.button<{ $irrelevant: boolean }>`
  background: none;
  border: 1px solid
    ${({ theme, $irrelevant }) =>
      $irrelevant ? theme.color.textMuted : theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme, $irrelevant }) =>
    $irrelevant ? theme.color.textMuted : theme.color.accent};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 1px 6px;
  white-space: nowrap;

  &:hover {
    background: ${({ theme, $irrelevant }) =>
      $irrelevant ? theme.color.background : theme.color.accentMuted};
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.error};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.md};
  padding: 2px 6px;
  line-height: 1;

  &:hover {
    border-color: ${({ theme }) => theme.color.error};
    background: ${({ theme }) => theme.color.errorMuted};
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const TimelineWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} 0;
  border-top: 1px solid ${({ theme }) => theme.color.border};
  margin-top: ${({ theme }) => theme.space.md};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SECTION_TYPE_LABELS: Record<string, string> = {
  intro: "Intro",
  as_is: "As-Is",
  to_be: "To-Be",
  discussion: "Discusión",
  decisions: "Decisiones",
  tasks: "Tareas",
  closing: "Cierre",
  other: "Otro",
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDuration(start: number, end: number): string {
  return `${formatTime(start)} → ${formatTime(end)} (${(end - start).toFixed(0)}s)`;
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

// ── Fase 50 — Tab Segmentación interactivo ─────────────────────────────────

function SectionsTab({
  doc,
  edits,
  onEdit,
  onDelete,
  onAdd,
  selectedId,
  onSelect,
  playingId,
  onPlay,
  progress,
}: {
  doc: StructureDoc;
  edits: Map<string, SectionEdit>;
  onEdit: (id: string, edit: SectionEdit) => void;
  onDelete: (id: string) => void;
  onAdd: () => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  playingId: string | null;
  onPlay: (id: string, startTime: number) => void;
  progress: number;
}) {
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");

  const sorted = [...doc.sections].sort((a, b) => a.start - b.start);
  const totalStart = sorted.length > 0 ? sorted[0].start : 0;
  const totalEnd = sorted.length > 0 ? sorted[sorted.length - 1].end : 1;

  const startTitleEdit = (section: MeetingSection) => {
    const edit = edits.get(section.id);
    setEditingTitleId(section.id);
    setEditingTitleValue(edit?.title ?? section.title);
  };

  const commitTitleEdit = (id: string) => {
    const section = doc.sections.find((s) => s.id === id);
    const newTitle = editingTitleValue.trim();
    if (section && newTitle && newTitle !== section.title) {
      onEdit(id, { title: newTitle });
    } else if (section && newTitle === section.title) {
      // Si volvió al original, limpia el edit de título
      const existing = edits.get(id);
      if (existing?.title !== undefined) {
        const rest = { ...existing };
        delete rest.title;
        onEdit(id, rest);
      }
    }
    setEditingTitleId(null);
  };

  const cancelTitleEdit = () => setEditingTitleId(null);

  const toggleRelevance = (section: MeetingSection) => {
    const edit = edits.get(section.id);
    const currentRelevant =
      edit?.isRelevant !== undefined ? edit.isRelevant : section.isRelevant;
    const newRelevant = !currentRelevant;

    if (newRelevant === section.isRelevant) {
      // Volvió al original, remover edit de isRelevant
      const existing = edits.get(section.id);
      if (existing) {
        const rest = { ...existing };
        delete rest.isRelevant;
        if (Object.keys(rest).length === 0) {
          onEdit(section.id, {}); // lo limpia abajo
          return;
        }
        onEdit(section.id, rest);
      }
    } else {
      onEdit(section.id, { ...edit, isRelevant: newRelevant });
    }
  };

  const getTitle = (section: MeetingSection): string => {
    const edit = edits.get(section.id);
    return edit?.title ?? section.title;
  };

  const getRelevance = (section: MeetingSection): boolean => {
    const edit = edits.get(section.id);
    return edit?.isRelevant !== undefined
      ? edit.isRelevant
      : section.isRelevant;
  };

  if (sorted.length === 0) {
    return (
      <>
        <Empty>No hay secciones detectadas.</Empty>
        <ToolbarRow>
          <Button $variant="ghost" onClick={onAdd}>
            + Agregar sección
          </Button>
        </ToolbarRow>
      </>
    );
  }

  return (
    <>
      <TimelineWrapper>
        <MeetingTimeline
          sections={sorted}
          totalStart={totalStart}
          totalEnd={totalEnd}
          selectedId={selectedId}
          playingId={playingId}
          progress={progress}
          edits={edits}
          onSelect={(id) => {
            onSelect(id);
            // scroll a la fila correspondiente
            const row = document.getElementById(`section-row-${id}`);
            row?.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }}
          onPlay={onPlay}
        />
      </TimelineWrapper>

      <ToolbarRow>
        <Button $variant="ghost" onClick={onAdd}>
          + Agregar sección
        </Button>
      </ToolbarRow>

      <DataTable>
        <thead>
          <tr>
            <Th>#</Th>
            <Th></Th>
            <Th>Título</Th>
            <Th>Tipo</Th>
            <Th>Timeline</Th>
            <Th>Participantes</Th>
            <Th></Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((section, i) => {
            const isRelevant = getRelevance(section);
            const isSelected = selectedId === section.id;
            const isEditing = editingTitleId === section.id;

            return (
              <tr
                key={section.id}
                id={`section-row-${section.id}`}
                style={{
                  background: isSelected
                    ? "var(--color-accent-muted, rgba(59,130,246,0.08))"
                    : undefined,
                }}
                onClick={() => onSelect(section.id)}
              >
                <Td>{i + 1}</Td>
                <Td>
                  <PlayButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onPlay(section.id, section.start);
                    }}
                    title="Escuchar 10s desde el inicio de esta sección"
                  >
                    ▶
                  </PlayButton>
                </Td>
                <Td>
                  {isEditing ? (
                    <InlineInput
                      value={editingTitleValue}
                      onChange={(e) =>
                        setEditingTitleValue(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          commitTitleEdit(section.id);
                        if (e.key === "Escape") cancelTitleEdit();
                      }}
                      onBlur={() => commitTitleEdit(section.id)}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <ClickableTitle
                      onClick={(e) => {
                        e.stopPropagation();
                        startTitleEdit(section);
                      }}
                      title="Clic para renombrar"
                    >
                      {getTitle(section)}
                      {!isRelevant && (
                        <Badge
                          $color="#6b7280"
                          style={{ marginLeft: 8 }}
                        >
                          No relevante
                        </Badge>
                      )}
                    </ClickableTitle>
                  )}
                </Td>
                <Td>
                  <Badge
                    $color={
                      SECTION_TYPE_COLORS[section.type] ?? "#9ca3af"
                    }
                  >
                    {SECTION_TYPE_LABELS[section.type] ?? section.type}
                  </Badge>
                </Td>
                <Td>
                  {formatDuration(section.start, section.end)}
                </Td>
                <Td>
                  {(() => {
                    const names = section.mainParticipantIds
                      .map(
                        (pid) =>
                          doc.activeParticipants.find(
                            (p) => p.id === pid,
                          )?.name,
                      )
                      .filter(Boolean);
                    return names.length > 0
                      ? names.join(", ")
                      : "—";
                  })()}
                </Td>
                <Td>
                  <RelevanceBadge
                    $irrelevant={!isRelevant}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRelevance(section);
                    }}
                    title={
                      isRelevant
                        ? "Marcar como no relevante"
                        : "Marcar como relevante"
                    }
                  >
                    {isRelevant ? "Relevante" : "No relevante"}
                  </RelevanceBadge>
                </Td>
                <Td>
                  <DeleteButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(section.id);
                    }}
                    title="Eliminar sección"
                  >
                    🗑
                  </DeleteButton>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>
    </>
  );
}

// ── Fase 51 — Tab Decisiones interactivo ────────────────────────────────────

const CONFIDENCE_PRIORITY: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

function DecisionsTab({
  doc,
  edits,
  onEdit,
  playingId,
  onPlay,
  progress,
}: {
  doc: StructureDoc;
  edits: Map<string, DecisionEdit>;
  onEdit: (id: string, edit: DecisionEdit) => void;
  playingId: string | null;
  onPlay: (id: string, timestamp: number) => void;
  progress: number;
}) {
  if (doc.decisions.length === 0) {
    return <Empty>No hay decisiones detectadas.</Empty>;
  }

  const sorted = [...doc.decisions].sort(
    (a, b) =>
      (CONFIDENCE_PRIORITY[a.confidence] ?? 2) -
      (CONFIDENCE_PRIORITY[b.confidence] ?? 2),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {sorted.map((d) => (
        <DecisionCard
          key={d.id}
          decision={d}
          index={doc.decisions.indexOf(d)}
          doc={doc}
          edit={edits.get(d.id)}
          playingId={playingId}
          progress={progress}
          onPlay={onPlay}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
}

// ── Fase 52 — Tab Tareas interactivo ────────────────────────────────────────

function TasksTab({
  doc,
  edits,
  onEdit,
  onAdd,
  playingId,
  onPlay,
  progress,
}: {
  doc: StructureDoc;
  edits: Map<string, TaskEdit>;
  onEdit: (id: string, edit: TaskEdit) => void;
  onAdd: () => void;
  playingId: string | null;
  onPlay: (id: string, timestamp: number) => void;
  progress: number;
}) {
  const sorted = [...doc.tasks].sort(
    (a, b) => a.timestamp - b.timestamp,
  );

  if (sorted.length === 0) {
    return (
      <>
        <Empty>No hay tareas detectadas.</Empty>
        <ToolbarRow>
          <Button $variant="ghost" onClick={onAdd}>
            + Agregar tarea
          </Button>
        </ToolbarRow>
      </>
    );
  }

  return (
    <>
      <div
        style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
      >
        {sorted.map((t) => (
          <TaskCard
            key={t.id}
            task={t}
            index={doc.tasks.indexOf(t)}
            doc={doc}
            edit={edits.get(t.id)}
            playingId={playingId}
            progress={progress}
            onPlay={onPlay}
            onEdit={onEdit}
          />
        ))}
      </div>
      <ToolbarRow>
        <Button $variant="ghost" onClick={onAdd}>
          + Agregar tarea
        </Button>
      </ToolbarRow>
    </>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function StructureScreen() {
  const processingId = useAtomValue(currentProcessingIdAtom);
  const navigateTo = useNavigateTo();
  useSyncParams();
  const [doc, setDoc] = useAtom(structureDocAtom);
  const [loading, setLoading] = useAtom(structureLoadingAtom);
  const [error, setError] = useAtom(structureErrorAtom);
  const [edits, setEdits] = useAtom(structureSectionEditsAtom);
  const [decisionEdits, setDecisionEdits] = useAtom(
    structureDecisionEditsAtom,
  );
  const [taskEdits, setTaskEdits] = useAtom(structureTaskEditsAtom);
  const tab = useAtomValue(structureTabAtom);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const editsVersionRef = useRef(0);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Métricas (Fase 61) ────────────────────────────────────────────────────
  const getElapsed = useStepTimer();
  const tasksAddedRef = useRef(0);

  // ── Audio / sample player (Fase 50) ─────────────────────────────────────
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [sampleState, sampleControls] = useSamplePlayer(audioRef);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Cargar datos + audio al montar.
  useEffect(() => {
    if (!processingId) return;

    if (doc !== null) return;

    setLoading(true);
    setError(null);

    // Cargar structure.json
    const loadStructure = getProcessingFile(processingId, "structure.json")
      .then((info) => fetchTextFromPar(info.downloadUrl))
      .then((text) => {
        const parsed = JSON.parse(text) as StructureDoc;
        setDoc(parsed);
        setLoading(false);
      });

    // Cargar pipelineStatus (necesario para el botón de confirmar)
    const loadStatus = getProcessing(processingId)
      .then((meta) => {
        setPipelineStatus(meta.pipelineStatus ?? null);
        return meta;
      })
      .catch((err) => {
        console.warn(
          "[StructureScreen] No se pudo leer el meta:",
          err,
        );
        setPipelineStatus(null);
      });

    // Cargar audio en paralelo
    const loadAudio = loadStatus
      .then((meta) => {
        if (!meta) return;
        const fn = meta.audioFilename ?? "audio.wav";
        return getProcessingFile(processingId, fn);
      })
      .then((fileInfo) => {
        if (fileInfo) setAudioUrl(fileInfo.downloadUrl);
      })
      .catch((err) => {
        console.warn(
          "[StructureScreen] No se pudo cargar el audio:",
          err,
        );
      });

    // Cargar edits guardados (solo después de que structure.json y status estén listos)
    const loadEdits = Promise.all([loadStructure, loadStatus])
      .then(() => fetchStructureEdits(processingId))
      .then((body) => {
        editsVersionRef.current = body.version;
        setEdits(new Map(Object.entries(body.sections)));
        setDecisionEdits(new Map(Object.entries(body.decisions)));
        setTaskEdits(new Map(Object.entries(body.tasks)));
      })
      .catch((err) => {
        // 404 = no hay edits guardados todavía, es正常
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) return;
        console.warn("[StructureScreen] No se pudieron cargar edits:", err);
      });

    Promise.all([loadStructure, loadStatus, loadAudio, loadEdits]).catch((err) => {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setLoading(false);
    });
  }, [processingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Autosave de edits (Fase 53b) ──────────────────────────────────────
  useEffect(() => {
    if (!processingId || !doc) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      const body = {
        version: editsVersionRef.current,
        sections: Object.fromEntries(edits),
        decisions: Object.fromEntries(decisionEdits),
        tasks: Object.fromEntries(taskEdits),
      };
      saveStructureEdits(processingId, body)
        .then((res) => {
          editsVersionRef.current = res.version;
        })
        .catch((err) => {
          console.warn("[StructureScreen] No se pudieron guardar edits:", err);
        });
    }, 2000);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [processingId, doc, edits, decisionEdits, taskEdits]);

  const handlePlaySection = useCallback(
    (id: string, startTime: number) => {
      sampleControls.play(id, startTime, { duration: 10 });
    },
    [sampleControls],
  );

  const handleEdit = useCallback(
    (id: string, edit: SectionEdit) => {
      setEdits((prev) => {
        const next = new Map(prev);
        // Si el objeto está vacío, elimina la entrada
        if (Object.keys(edit).length === 0) {
          next.delete(id);
        } else {
          next.set(id, edit);
        }
        return next;
      });
    },
    [setEdits],
  );

  const handleDecisionEdit = useCallback(
    (id: string, edit: DecisionEdit) => {
      setDecisionEdits((prev) => {
        const next = new Map(prev);
        if (Object.keys(edit).length === 0) {
          next.delete(id);
        } else {
          next.set(id, edit);
        }
        return next;
      });
    },
    [setDecisionEdits],
  );

  const handleTaskEdit = useCallback(
    (id: string, edit: TaskEdit) => {
      setTaskEdits((prev) => {
        const next = new Map(prev);
        if (Object.keys(edit).length === 0) {
          next.delete(id);
        } else {
          next.set(id, edit);
        }
        return next;
      });
    },
    [setTaskEdits],
  );

  const handleTaskAdd = useCallback(() => {
    if (!doc) return;
    const firstSection = doc.sections[0];
    const newTask = {
      id: crypto.randomUUID(),
      text: "Nueva tarea",
      responsibleId: null,
      responsibleName: "",
      deadline: null,
      timestamp: firstSection?.start ?? 0,
      sectionId: firstSection?.id ?? "",
      requiresNotification: false,
    };
    setDoc({ ...doc, tasks: [...doc.tasks, newTask] });
    tasksAddedRef.current += 1;
  }, [doc, setDoc]);

  const handleConfirmStructure = useCallback(async () => {
    if (!processingId || !doc || confirming) return;

    setConfirming(true);
    setConfirmError(null);

    // Enviar métricas de P4 (non-fatal)
    let decisionsConfirmed = 0;
    let decisionsDismissed = 0;
    for (const e of decisionEdits.values()) {
      if (e.kind === "confirm") decisionsConfirmed++;
      else if (e.kind === "dismiss") decisionsDismissed++;
    }
    let tasksConfirmed = 0;
    for (const e of taskEdits.values()) {
      if (e.kind === "confirm") tasksConfirmed++;
    }
    submitMetrics(processingId, {
      timeSpent: { p4: getElapsed() },
      counts: {
        decisionsConfirmed,
        decisionsDismissed,
        tasksConfirmed,
        tasksAdded: tasksAddedRef.current,
      },
    }).catch(() => {/* non-fatal */});

    try {
      const finalDoc: StructureDoc = {
        ...doc,
        generatedAt: new Date().toISOString(),
        sections: doc.sections.map((s) => {
          const edit = edits.get(s.id);
          if (!edit) return s;
          return {
            ...s,
            ...(edit.title !== undefined ? { title: edit.title } : {}),
            ...(edit.isRelevant !== undefined
              ? { isRelevant: edit.isRelevant }
              : {}),
          };
        }),
        decisions: doc.decisions
          .filter((d) => {
            const edit = decisionEdits.get(d.id);
            return edit?.kind !== "dismiss";
          })
          .map((d) => {
            const edit = decisionEdits.get(d.id);
            if (!edit) return d;
            return {
              ...d,
              ...(edit.text !== undefined ? { text: edit.text } : {}),
            };
          }),
        tasks: doc.tasks
          .filter((t) => {
            const edit = taskEdits.get(t.id);
            return edit?.kind !== "dismiss";
          })
          .map((t) => {
            const edit = taskEdits.get(t.id);
            if (!edit) return t;
            return {
              ...t,
              ...(edit.text !== undefined ? { text: edit.text } : {}),
              ...(edit.responsibleName !== undefined
                ? { responsibleName: edit.responsibleName }
                : {}),
              ...(edit.deadline !== undefined
                ? { deadline: edit.deadline || null }
                : {}),
              ...(edit.requiresNotification !== undefined
                ? { requiresNotification: edit.requiresNotification }
                : {}),
            };
          }),
      };

      await confirmStructure(processingId, finalDoc);
      // Limpiar edits guardados
      saveStructureEdits(processingId, {
        version: editsVersionRef.current,
        sections: {},
        decisions: {},
        tasks: {},
      }).catch(() => {});
      navigateTo.meetings();
    } catch (err) {
      setConfirmError(
        err instanceof Error
          ? err.message
          : "Error al confirmar la estructura.",
      );
    } finally {
      setConfirming(false);
    }
  }, [
    processingId,
    doc,
    edits,
    decisionEdits,
    taskEdits,
    navigateTo.meetings,
    confirming,
  ]);

  const handleAdd = useCallback(() => {
    if (!doc) return;
    const sorted = [...doc.sections].sort((a, b) => a.start - b.start);
    const lastEnd =
      sorted.length > 0 ? sorted[sorted.length - 1].end : 0;
    const newSection: MeetingSection = {
      id: crypto.randomUUID(),
      title: "Nueva sección",
      type: "other",
      start: lastEnd,
      end: lastEnd + 60,
      mainParticipantIds: [],
      isRelevant: true,
    };
    setDoc({ ...doc, sections: [...doc.sections, newSection] });
    setSelectedId(newSection.id);
  }, [doc, setDoc]);

  const handleDelete = useCallback(
    (id: string) => {
      if (!doc) return;
      setDoc({
        ...doc,
        sections: doc.sections.filter((s) => s.id !== id),
      });
      setEdits((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      if (selectedId === id) setSelectedId(null);
    },
    [doc, setDoc, setEdits, selectedId],
  );

  if (!processingId) {
    return (
      <Wrapper>
        <ErrorBox>No hay reunión seleccionada.</ErrorBox>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          controls={false}
        />
      )}
      <Header>
        <Title>Estructura de la reunión</Title>
        <Button $variant="ghost" onClick={() => navigateTo.meetings()}>
          ← Volver
        </Button>
      </Header>

      <TabBar>
        <TabButton
          $active={tab === "sections"}
          onClick={() => processingId && navigateTo.structure(processingId, "sections")}
        >
          Segmentación
          {doc && <CountBadge>({doc.sections.length})</CountBadge>}
        </TabButton>
        <TabButton
          $active={tab === "decisions"}
          onClick={() => processingId && navigateTo.structure(processingId, "decisions")}
        >
          Decisiones
          {doc && <CountBadge>({doc.decisions.length})</CountBadge>}
        </TabButton>
        <TabButton
          $active={tab === "tasks"}
          onClick={() => processingId && navigateTo.structure(processingId, "tasks")}
        >
          Tareas
          {doc && <CountBadge>({doc.tasks.length})</CountBadge>}
        </TabButton>
      </TabBar>

      {loading && <Loading>Cargando estructura de la reunión…</Loading>}
      {error && <ErrorBox>{error}</ErrorBox>}

      {doc && (
        <TabContent>
          {tab === "sections" && (
            <SectionsTab
              doc={doc}
              edits={edits}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={handleAdd}
              selectedId={selectedId}
              onSelect={setSelectedId}
              playingId={sampleState.playingId}
              onPlay={handlePlaySection}
              progress={sampleState.progress}
            />
          )}
          {tab === "decisions" && (
            <DecisionsTab
              doc={doc}
              edits={decisionEdits}
              onEdit={handleDecisionEdit}
              playingId={sampleState.playingId}
              onPlay={handlePlaySection}
              progress={sampleState.progress}
            />
          )}
          {tab === "tasks" && (
            <TasksTab
              doc={doc}
              edits={taskEdits}
              onEdit={handleTaskEdit}
              onAdd={handleTaskAdd}
              playingId={sampleState.playingId}
              onPlay={handlePlaySection}
              progress={sampleState.progress}
            />
          )}
        </TabContent>
      )}
      {doc && pipelineStatus === "analyzed" && (
        <>
          {confirmError && <ErrorBox>{confirmError}</ErrorBox>}
          <Footer>
            <Button
              $variant="primary"
              onClick={handleConfirmStructure}
              disabled={confirming}
            >
              {confirming
                ? "Confirmando…"
                : "✓ Confirmar estructura"}
            </Button>
          </Footer>
        </>
      )}
      {doc && pipelineStatus !== null && pipelineStatus !== "analyzed" && (
        <Empty>
          La estructura ya fue confirmada (estado: {pipelineStatus}). El
          botón de confirmar solo aparece cuando el pipeline está en
          "analyzed".
        </Empty>
      )}
      {doc && pipelineStatus === null && (
        <Loading>Cargando estado del pipeline…</Loading>
      )}
    </Wrapper>
  );
}
